// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from 'react';
import { authService } from '../services/appwriteService';

// Créer le contexte d'authentification
const AuthContext = createContext();

// Hook pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      console.log('🔍 Vérification état authentification...');
      
      const currentUser = await authService.getCurrentUser();
      
      if (currentUser) {
        console.log('✅ Utilisateur connecté:', currentUser.profile.email);
        setUser(currentUser.user);
        setProfile(currentUser.profile);
      } else {
        console.log('❌ Aucun utilisateur connecté');
        setUser(null);
        setProfile(null);
      }
    } catch (error) {
      console.error('💥 Erreur vérification auth:', error);
      setError('Erreur de vérification de l\'authentification');
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de connexion
  const signIn = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      console.log('🚀 Tentative de connexion:', email);
      
      const result = await authService.signIn(email, password);
      
      console.log('✅ Connexion réussie:', result.profile.role);
      setUser(result.user);
      setProfile(result.profile);
      
      return { success: true };
    } catch (error) {
      console.error('💥 Erreur connexion:', error);
      const errorMessage = error.message || 'Erreur de connexion';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const signOut = async () => {
    try {
      console.log('🔓 Déconnexion...');
      await authService.signOut();
      setUser(null);
      setProfile(null);
      setError(null);
      console.log('✅ Déconnexion réussie');
    } catch (error) {
      console.error('💥 Erreur déconnexion:', error);
      setError('Erreur lors de la déconnexion');
    }
  };

  // Vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return profile?.role === 'admin';
  };

  // Vérifier si l'utilisateur est auditeur
  const isAuditor = () => {
    return profile?.role === 'auditor';
  };

  // Vérifier si l'utilisateur est actif
  const isActive = () => {
    return profile?.is_active === true;
  };

  // Valeurs exposées par le contexte
  const value = {
    user,
    profile,
    loading,
    error,
    signIn,
    signOut,
    isAdmin,
    isAuditor,
    isActive,
    checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};